/* rh-size-prompt: "Which size?" prompt above search / collection results.

   How it works
   - Dawn renders the Size facet with every size value present in the current
     result set (plus counts). We read those inputs to work out which size
     system the results use (letters, numeric apparel, waist, shoe, kids).
   - Instead of our own chip list, the shopper picks from Dawn's own Size
     filter. On desktop the Size dropdown is opened automatically once per
     session (with a short hint inside it); the slim bar above the grid has a
     "Choose your size" button that opens it again (on mobile it opens the
     filter drawer on the Size panel). The values inside the Size filter are
     sorted into a sensible order (XXS -> XXXL, then numbers).
   - When a size is ticked in the filter, the hidden "In stock" availability
     box in the same form is ticked too, so the grid only shows products
     with that size actually in stock (variant filters combine per variant).
   - The choice is remembered per size system in localStorage. On a later
     search that uses the same system and still has that value in stock, it
     is applied automatically and the bar shows "Showing size X in stock /
     Change / Show all sizes". If the saved size is not in those results,
     the shopper is asked instead of being filtered to nothing.
   - If a gender facet exists (any filter input whose name contains
     "gender") and the results span more than one gender, the bar asks
     Women / Men once and remembers it.
   - Skip / Show all set a per-session flag for that size system so the
     shopper is not nagged again this visit; a small "Set my size" link stays.
   - Dawn re-renders #ProductGridContainer and the facets on filter/sort
     changes; a MutationObserver re-renders the bar from the fresh facets.
*/
(function () {
  if (window.__rhSizePrompt) return;
  window.__rhSizePrompt = true;

  var LS_SIZES = 'rh_sizes';
  var LS_GENDER = 'rh_gender';
  var SIZE_PARAM = 'filter.v.option.size';
  var AVAIL_PARAM = 'filter.v.availability';
  var DESKTOP = '(min-width: 990px)'; // Dawn's breakpoint for the desktop facet bar
  var HINT = 'Pick your size and we’ll only show what’s in stock — we’ll remember it for next time.';

  var SHOE_WORDS = /sneaker|shoe|boot|heel|sandal|loafer|flat|mule|clog|slipper|footwear|oxford|pump|wedge|espadrille|slide/i;
  var LETTER_RANK = { xxxs: 0, xxs: 1, xs: 2, s: 3, m: 4, l: 5, xl: 6, xxl: 7, xxxl: 8, xxxxl: 9, xxxxxl: 10 };
  var LETTER_MAP = {
    '3xs': 'xxxs', '2xs': 'xxs', '2xl': 'xxl', '3xl': 'xxxl', '4xl': 'xxxxl', '5xl': 'xxxxxl',
    '1x': 'xl', '2x': 'xxl', '3x': 'xxxl', '4x': 'xxxxl', '5x': 'xxxxxl',
    'x-small': 'xs', 'xsmall': 'xs', 'small': 's', 'medium': 'm', 'large': 'l',
    'x-large': 'xl', 'xlarge': 'xl', 'xx-large': 'xxl', 'xxlarge': 'xxl', 'xxx-large': 'xxxl'
  };
  var SCALE_LABEL = { letter: 'size', numeric: 'size', waist: 'waist size', shoe: 'shoe size', kids: 'size' };

  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { if (v == null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch (e) {} }

  function norm(v) { return String(v || '').trim().toLowerCase(); }
  function isDesktop() { return window.matchMedia && window.matchMedia(DESKTOP).matches; }

  // ---- facet reading -------------------------------------------------------

  function readFacet(selector) {
    var seen = {};
    var inputs = document.querySelectorAll(selector);
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      var value = inp.value;
      if (!value) continue;
      var label = inp.closest('label') || inp.parentElement;
      var text = label ? label.textContent : '';
      // Dawn labels read "M (1000)" plus a visually hidden "(1000 products)"
      var m = text && text.match(/\((\d[\d,]*)(?: products?)?\)/);
      var count = m ? parseInt(m[1].replace(/,/g, ''), 10) : 1;
      var nameEl = label && label.querySelector('.facet-checkbox__text-label');
      var display = nameEl ? nameEl.textContent.trim() : value;
      if (!seen[value] || seen[value].count < count) {
        seen[value] = { value: value, label: display, count: count, checked: inp.checked };
      }
    }
    var out = [];
    for (var k in seen) out.push(seen[k]);
    return out;
  }

  function readSizes() { return readFacet('input[name="' + SIZE_PARAM + '"]'); }

  function readGender() {
    var inputs = document.querySelectorAll('input[name^="filter.p."]');
    var name = null;
    for (var i = 0; i < inputs.length; i++) {
      if (/gender/i.test(inputs[i].name)) { name = inputs[i].name; break; }
    }
    if (!name) return null;
    return { name: name, values: readFacet('input[name="' + name + '"]') };
  }

  function shoeShare() {
    // How much of the result set sits in footwear categories, judged from
    // the product-level facets (category / product type) Dawn renders.
    var inputs = document.querySelectorAll('input[name^="filter.p."]');
    var total = 0, shoe = 0, seen = {};
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (/gender|vendor|price|tag/i.test(inp.name)) continue;
      var key = inp.name + '=' + inp.value;
      if (seen[key]) continue;
      seen[key] = 1;
      var label = inp.closest('label') || inp.parentElement;
      var text = label ? label.textContent : '';
      var m = text.match(/\((\d[\d,]*)(?: products?)?\)/);
      var count = m ? parseInt(m[1].replace(/,/g, ''), 10) : 0;
      total += count;
      if (SHOE_WORDS.test(text)) shoe += count;
    }
    return total ? shoe / total : 0;
  }

  // ---- size system detection ----------------------------------------------

  function classify(entries, shoeMode) {
    var groups = { letter: [], numeric: [], waist: [], shoe: [], kids: [] };
    entries.forEach(function (e) {
      // Classify by the visible label: grouped values from Search & Discovery
      // carry a gid://shopify/FilterSettingGroup/... value but a normal label.
      var k = norm(e.label || e.value);
      var scale = null, sortKey = 0;
      if (/^(xxxs|xxs|xs|s|m|l|xl|xxl|xxxl|xxxxl|\dxl|\dxs|[1-6]x|xs\/s|s\/m|m\/l|l\/xl|xl\/xxl|x-?small|small|medium|large|x-?large|xx-?large|xxx-?large)$/.test(k)) {
        scale = 'letter';
        var base = LETTER_MAP[k] || k;
        sortKey = base in LETTER_RANK ? LETTER_RANK[base] : 20;
        if (/^[1-6]x$/.test(k)) sortKey += 0.3;  // 2x sits after XXL
        if (k.indexOf('/') > -1) sortKey += 0.5;
      } else if (/^(\d+-\d+m|\d+m|\d+t|\d+y|\d+-\d+y|nb|newborn|\d+-\d+)$/.test(k)) {
        scale = 'kids';
        sortKey = parseFloat(k.replace(/^nb|newborn/, '0')) || 0;
        if (/m$/.test(k)) sortKey -= 100; // months before years/toddler
      } else {
        var m = k.match(/^(?:us|eu|uk)?\s*(\d{1,2}(?:\.5)?)$/);
        var w = k.match(/^(\d{2})\s*[x\/]\s*\d{2}$/);
        if (w) {
          scale = 'waist';
          sortKey = parseInt(w[1], 10) + 0.1;
        } else if (m) {
          var n = parseFloat(m[1]);
          sortKey = n;
          var shoeLike = /^(us|eu|uk)/.test(k) || /\.5$/.test(k); // us8, 7.5 are shoes wherever they appear
          if (shoeLike || (shoeMode && ((n >= 4 && n <= 16) || (n >= 34 && n <= 50)))) scale = 'shoe'; // US 4-16, EU 34-50
          else if (n >= 23 && n <= 50) scale = 'waist';
          else scale = 'numeric';
        }
      }
      if (scale) groups[scale].push({ value: e.value, label: e.label, count: e.count, sortKey: sortKey, checked: e.checked });
    });
    return groups;
  }

  // Scales present in the result set, biggest first, as [{scale, total}].
  function pickScales(groups) {
    var out = [];
    for (var scale in groups) {
      var total = 0;
      groups[scale].forEach(function (x) { total += x.count; });
      if (total > 0) out.push({ scale: scale, total: total });
    }
    out.sort(function (a, b) { return b.total - a.total; });
    return out;
  }

  // Plain integers 4-16 are shoes only when the results are mostly footwear;
  // otherwise they are numeric apparel sizes (women's 0-16).
  function detectShoeMode() {
    return shoeShare() > 0.4;
  }

  // Display order for the values inside Dawn's Size filter: letter sizes in
  // order, then numbers ascending, then everything else alphabetically.
  function displayRank(label) {
    var k = norm(label);
    var base = LETTER_MAP[k] || k;
    if (base in LETTER_RANK) return [0, LETTER_RANK[base] + (/^[1-6]x$/.test(k) ? 0.3 : 0), k];
    var slash = k.match(/^(xxs|xs|s|m|l|xl|xxl)\/(xs|s|m|l|xl|xxl|xxxl)$/);
    if (slash) return [0, LETTER_RANK[slash[1]] + 0.5, k];
    var num = k.match(/^(?:us|eu|uk)?\s*(\d{1,2}(?:\.5)?)(?:\s*[x\/]\s*\d{2})?$/);
    if (num) return [1, parseFloat(num[1]) + (/^(us|eu|uk)/.test(k) ? 0.01 : 0), k];
    var kids = k.match(/^(\d+)(?:-\d+)?(m|t|y)?$/);
    if (kids) return [2, parseFloat(kids[1]) + (kids[2] === 'm' ? -100 : 0), k];
    return [3, 0, k];
  }

  function cmpRank(a, b) {
    if (a[0] !== b[0]) return a[0] - b[0];
    if (a[1] !== b[1]) return a[1] - b[1];
    return a[2] < b[2] ? -1 : a[2] > b[2] ? 1 : 0;
  }

  // ---- url helpers ----------------------------------------------------------

  function currentSizes() {
    var u = new URL(location.href);
    return u.searchParams.getAll(SIZE_PARAM);
  }

  function urlWith(mutator) {
    var u = new URL(location.href);
    u.searchParams.delete('page');
    mutator(u.searchParams);
    return u.toString();
  }

  function go(url) { location.assign(url); }

  // ---- Dawn's Size filter ----------------------------------------------------

  function sizeDetails(mobile) {
    var prefix = mobile ? 'Details-Mobile-' : 'Details-';
    return document.querySelector('details[id^="' + prefix + SIZE_PARAM + '-"]');
  }

  // Sort the values and add the hint inside Dawn's Size filter (desktop
  // dropdown and mobile drawer panel). Idempotent: Dawn replaces the inner
  // HTML of a facet on re-render, which drops our markers, so this is run
  // after every render.
  function decorateSizeFacet() {
    [sizeDetails(false), sizeDetails(true)].forEach(function (d) {
      if (!d) return;
      var first = d.querySelector('input[name="' + SIZE_PARAM + '"]');
      var list = first && first.closest('ul');
      if (list && !list.hasAttribute('data-rh-sorted')) {
        var items = [].slice.call(list.children);
        items.forEach(function (li) {
          var lab = li.querySelector('.facet-checkbox__text-label');
          var inp = li.querySelector('input');
          li.__rhRank = displayRank(lab ? lab.textContent : inp ? inp.value : li.textContent);
        });
        items.sort(function (a, b) { return cmpRank(a.__rhRank, b.__rhRank); });
        items.forEach(function (li) { list.appendChild(li); });
        list.setAttribute('data-rh-sorted', '1');
      }
      if (!d.querySelector('.rh-sp__hint')) {
        var hint = el('p', 'rh-sp__hint', HINT);
        var header = d.querySelector('.facets__header');
        if (header) header.parentNode.insertBefore(hint, header);
        else if (list) list.parentNode.insertBefore(hint, list);
      }
    });
  }

  function openDesktopSize(focusFirst) {
    var d = sizeDetails(false);
    if (!d) return false;
    d.open = true;
    var s = d.querySelector('summary');
    if (s) s.setAttribute('aria-expanded', 'true');
    if (focusFirst) {
      var inp = d.querySelector('input[name="' + SIZE_PARAM + '"]');
      if (inp) { try { inp.focus({ preventScroll: true }); } catch (e) {} }
    }
    return true;
  }

  function openMobileSize() {
    var wrap = document.querySelector('.mobile-facets__wrapper');
    var drawer = wrap && wrap.querySelector('details.mobile-facets__disclosure');
    var d = sizeDetails(true);
    if (!drawer || !d) return false;
    // Go through Dawn's own summary handlers so its drawer animation, focus
    // trap and body scroll lock all behave.
    var ds = drawer.querySelector('summary');
    if (!drawer.open && ds) ds.click();
    setTimeout(function () {
      var s = d.querySelector('summary');
      if (!d.open && s) s.click();
    }, drawer.open ? 0 : 250);
    return true;
  }

  function openSizeFilter() {
    if (isDesktop()) {
      if (openDesktopSize(true)) {
        var d = sizeDetails(false);
        var r = d.getBoundingClientRect();
        if (r.top < 0 || r.top > window.innerHeight - 200) d.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    } else {
      openMobileSize();
    }
  }

  // Ticking a size in Dawn's filter also ticks the hidden "In stock" box in
  // the same form, so the request Dawn builds (FormData of the form, after
  // its debounce) carries filter.v.availability=1 as well.
  function syncAvailability(form) {
    if (!form) return;
    var anySize = form.querySelector('input[name="' + SIZE_PARAM + '"]:checked');
    var inStock = form.querySelector('input[name="' + AVAIL_PARAM + '"][value="1"]');
    if (inStock && anySize && !inStock.checked) inStock.checked = true;
  }

  // ---- rendering --------------------------------------------------------------

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function chip(label, onClick, active) {
    var b = el('button', 'rh-sp__chip' + (active ? ' rh-sp__chip--active' : ''), label);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
  }

  function link(label, onClick) {
    var b = el('button', 'rh-sp__link', label);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
  }

  function chooseButton(label) {
    var b = el('button', 'rh-sp__btn');
    b.type = 'button';
    b.appendChild(el('span', null, label));
    var caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    caret.setAttribute('viewBox', '0 0 10 6');
    caret.setAttribute('aria-hidden', 'true');
    caret.setAttribute('class', 'rh-sp__caret');
    caret.innerHTML = '<path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 1 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708z"/>';
    b.appendChild(caret);
    b.addEventListener('click', openSizeFilter);
    return b;
  }

  function mount() {
    var host = document.getElementById('ProductGridContainer') || (document.getElementById('product-grid') || {}).parentElement;
    if (!host) return null;
    var existing = document.getElementById('rh-size-prompt');
    if (existing) existing.parentNode.removeChild(existing);
    // Outer wrapper borrows Dawn's .page-width so the bar lines up with the
    // grid; the grey box is the inner element.
    var outer = el('div', 'rh-sp-wrap page-width');
    outer.id = 'rh-size-prompt';
    var bar = el('div', 'rh-sp');
    outer.appendChild(bar);
    host.insertBefore(outer, host.firstChild);
    return bar;
  }

  var rendering = false;
  var lastSig = null;

  // Signature of everything the bar depends on. The product grid mutates
  // constantly (wishlist hearts, lazy images), and rebuilding the bar on every
  // mutation replaced the button under the shopper's finger between mousedown
  // and mouseup, so clicks never fired. Only rebuild when this changes.
  function signature(sizes, gender) {
    return location.href + '|' + sizes.map(function (s) { return s.value + ':' + s.count; }).join(',') +
      '|' + (gender ? gender.name + ':' + gender.values.map(function (v) { return v.value; }).join(',') : '');
  }

  function render() {
    if (rendering) return;
    rendering = true;
    try { decorateSizeFacet(); renderInner(); } finally { setTimeout(function () { rendering = false; }, 0); }
  }

  function renderInner() {
    var sizes = readSizes();
    var gender = readGender();
    if (!sizes.length && !gender) { var stale = document.getElementById('rh-size-prompt'); if (stale) stale.remove(); lastSig = null; return; }
    var sig = signature(sizes, gender);
    if (sig === lastSig && document.getElementById('rh-size-prompt')) return;
    lastSig = sig;

    var shoeMode = detectShoeMode();
    var groups = classify(sizes, shoeMode);
    var scales = pickScales(groups);
    var scale = scales.length ? scales[0].scale : null;
    // Results like "dress" carry both letter and numeric sizes. When the
    // runner-up system is at least 30% as common as the leader, treat both as
    // "in play" for auto-apply.
    var scalesShown = scales.filter(function (s, i) { return i === 0 || s.total >= scales[0].total * 0.3; }).slice(0, 2);
    var entries = [];
    for (var g in groups) groups[g].forEach(function (c) { entries.push({ value: c.value, label: c.label, scale: g }); });
    var labelFor = function (value) {
      var hit = entries.filter(function (c) { return c.value === value; })[0];
      return hit ? hit.label : value;
    };

    var saved = lsGet(LS_SIZES) || {};
    var applied = currentSizes();
    var skipKey = 'rh_skip:' + scale;
    var u = new URL(location.href);

    // Remember whatever size the shopper has applied (through Dawn's Size
    // filter or our auto-apply) under that value's own size system.
    if (applied.length === 1 && scale) {
      var hit = entries.filter(function (c) { return c.value === applied[0]; })[0];
      if (hit && saved[hit.scale] !== applied[0]) { saved[hit.scale] = applied[0]; lsSet(LS_SIZES, saved); }
    }

    // --- gender -----------------------------------------------------------
    var genderApplied = gender ? u.searchParams.getAll(gender.name) : [];
    var savedGender = lsGet(LS_GENDER);
    if (gender && gender.values.length > 1 && !genderApplied.length) {
      if (savedGender && gender.values.some(function (v) { return v.value === savedGender; })) {
        var gu = urlWith(function (p) { p.append(gender.name, savedGender); });
        if (!ssGet('rh_auto_gender:' + gu)) { ssSet('rh_auto_gender:' + gu, '1'); go(gu); return; }
      }
    }

    // --- auto-apply saved size ---------------------------------------------
    if (scale && !applied.length && !ssGet(skipKey)) {
      for (var si = 0; si < scalesShown.length; si++) {
        var sv = saved[scalesShown[si].scale];
        if (!sv) continue;
        var inStock = entries.some(function (c) { return c.value === sv; });
        if (!inStock) continue;
        var su = urlWith(function (p) { p.append(SIZE_PARAM, sv); p.set(AVAIL_PARAM, '1'); });
        if (!ssGet('rh_auto_size:' + su)) { ssSet('rh_auto_size:' + su, '1'); go(su); return; }
        break;
      }
    }

    var bar = mount();
    if (!bar) return;

    // gender row (only when it is a real choice here)
    if (gender && gender.values.length > 1 && !genderApplied.length && !savedGender) {
      var grow = el('div', 'rh-sp__row');
      grow.appendChild(el('span', 'rh-sp__q', 'Shopping for'));
      var gchips = el('div', 'rh-sp__chips');
      gender.values.forEach(function (v) {
        gchips.appendChild(chip(v.label, function () {
          lsSet(LS_GENDER, v.value);
          go(urlWith(function (p) { p.append(gender.name, v.value); }));
        }));
      });
      grow.appendChild(gchips);
      bar.appendChild(grow);
    }

    if (!scale) return;

    var question = scalesShown.length > 1 ? 'Which size?' : 'Which ' + (SCALE_LABEL[scale] || 'size') + '?';
    var row = el('div', 'rh-sp__row');

    var showAsk = function () {
      row.innerHTML = '';
      row.appendChild(el('span', 'rh-sp__q', question));
      row.appendChild(chooseButton('Choose your ' + (SCALE_LABEL[scale] || 'size')));
      row.appendChild(link('Skip', function () { ssSet(skipKey, '1'); showMinimal(); }));
    };
    var showApplied = function () {
      row.innerHTML = '';
      var txt = el('span', 'rh-sp__status');
      txt.appendChild(document.createTextNode('Showing ' + SCALE_LABEL[scale] + ' '));
      txt.appendChild(el('b', null, applied.map(labelFor).join(', ')));
      txt.appendChild(document.createTextNode(' in stock'));
      row.appendChild(txt);
      row.appendChild(link('Change', openSizeFilter));
      row.appendChild(link('Show all sizes', function () {
        ssSet(skipKey, '1');
        go(urlWith(function (p) { p.delete(SIZE_PARAM); p.delete(AVAIL_PARAM); }));
      }));
    };
    var showMinimal = function () {
      row.innerHTML = '';
      row.appendChild(link('Set my ' + SCALE_LABEL[scale], function () { ssSet(skipKey, null); openSizeFilter(); }));
    };

    if (applied.length) showApplied();
    else if (ssGet(skipKey)) showMinimal();
    else showAsk();

    bar.appendChild(row);

    // Desktop: open Dawn's Size dropdown for them, once per session, when
    // there is nothing applied yet and they have not skipped.
    if (!applied.length && !ssGet(skipKey) && isDesktop() && !ssGet('rh_auto_open')) {
      ssSet('rh_auto_open', '1');
      openDesktopSize(false);
    }
  }

  // ---- boot --------------------------------------------------------------------

  // The Availability facet is hidden from shoppers (CSS hides the dropdown;
  // this hides the "Availability: In stock" pill Dawn shows once it is
  // applied). The filter itself stays enabled so the size prompt can still
  // restrict results to in-stock sizes behind the scenes.
  function hideAvailabilityPills() {
    var pills = document.querySelectorAll('.active-facets__button');
    for (var i = 0; i < pills.length; i++) {
      if (/^\s*Availability\s*:/i.test(pills[i].textContent)) {
        var wrap = pills[i].closest('facet-remove') || pills[i];
        wrap.style.display = 'none';
      }
    }
  }

  var timer = null;
  function schedule() { clearTimeout(timer); timer = setTimeout(render, 80); }
  var pillTimer = null;
  function schedulePills() { clearTimeout(pillTimer); pillTimer = setTimeout(hideAvailabilityPills, 100); }

  function start() {
    render();
    hideAvailabilityPills();
    if ('MutationObserver' in window) {
      new MutationObserver(schedulePills).observe(document.body, { childList: true, subtree: true });
    }
    var grid = document.getElementById('ProductGridContainer');
    if (grid && 'MutationObserver' in window) {
      var isOurs = function (node) { return node && node.id === 'rh-size-prompt'; };
      new MutationObserver(function (records) {
        if (rendering) return;
        for (var i = 0; i < records.length; i++) {
          var r = records[i];
          var t = r.target;
          if (t && t.closest && t.closest('#rh-size-prompt')) continue;
          var ours = false;
          for (var a = 0; a < r.addedNodes.length; a++) if (isOurs(r.addedNodes[a])) ours = true;
          for (var b = 0; b < r.removedNodes.length; b++) if (isOurs(r.removedNodes[b])) ours = true;
          if (ours) continue;
          schedule();
          return;
        }
      }).observe(grid, { childList: true, subtree: true });
    }
    // Size ticked in Dawn's filter -> also tick "In stock" in that form.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name === SIZE_PARAM) {
        syncAvailability(t.closest('form'));
        if (t.checked) ssSet('rh_skip:' + (scaleOfValue(t.value) || ''), null);
      }
    }, true);
    window.addEventListener('popstate', schedule);
  }

  function scaleOfValue(value) {
    var groups = classify(readSizes(), detectShoeMode());
    for (var g in groups) for (var i = 0; i < groups[g].length; i++) if (groups[g][i].value === value) return g;
    return null;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
